import { formatPlateNumber } from '../../../utils/plateFormatter';
import React from 'react';
import Stat from '../../../ui/Stat/Stat';
import Badge from '../../../ui/Badge';
import StatusBadge from '../../../ui/Status/StatusBadge';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { Field as KitField } from '../../../ui/Field/Field';
import '../../../ui/DataTable/datatable.css';

// Old colour names used across the Traffic Fines tabs -> design-system tones
const STAT_TONE = { amber: 'warning', green: 'success', red: 'danger' };
const PILL_VARIANT = {
    slate: 'neutral', gray: 'neutral', green: 'success', amber: 'warning',
    red: 'danger', indigo: 'accent', purple: 'info',
};

/** KPI tile (kit Stat). tone colours the value: amber / green / red; indigo and slate stay neutral. */
export const StatCard = ({ label, value, sub, tone = 'indigo', onClick }) => (
    <Stat label={label} value={value} meta={sub} tone={STAT_TONE[tone]} onClick={onClick} />
);

/** Small tag (kit Badge). tone: slate | gray | green | amber | red | indigo | purple. */
export const Pill = ({ children, tone = 'slate', title, size = 'md', className = '' }) => (
    <Badge variant={PILL_VARIANT[tone] || 'neutral'} size={size} title={title} className={className}>{children}</Badge>
);

/** Paid / Unpaid as a status dot (kit StatusBadge). */
export const StatusPill = ({ status }) => (status === 'Paid'
    ? <StatusBadge status="paid" label="Paid" /> : <StatusBadge status="unpaid" label="Unpaid" />);

export const PAID_BY_LABEL = { company: 'Company', customer: 'Customer' };
const PAID_BY_VARIANT = { company: 'accent', customer: 'info' };

/** After "Paid": who settled it. Two toggles; clicking the active one clears it. */
export const PaidByControl = ({ fine, onChange, disabled }) => {
    if (fine.status !== 'Paid') return null;
    const current = fine.paid_by || '';
    if (current) {  // chosen: show only that party; click it to change
        return (
            <button type="button" disabled={disabled} title="Click to change who paid"
                    aria-label={`Paid by ${PAID_BY_LABEL[current]}: click to change`}
                    onClick={(e) => { e.stopPropagation(); onChange(''); }}
                    className={`yelo-badge yelo-badge--sm yelo-badge--${PAID_BY_VARIANT[current] || 'neutral'} cursor-pointer`}>
                {PAID_BY_LABEL[current]}
            </button>
        );
    }
    return (
        <span className="inline-flex items-center gap-1" title="Who paid this fine" role="group" aria-label="Who paid this fine">
            {['company', 'customer'].map((who) => (
                <button key={who} type="button" disabled={disabled}
                        onClick={(e) => { e.stopPropagation(); onChange(current === who ? '' : who); }}
                        className="yelo-badge yelo-badge--sm yelo-badge--neutral cursor-pointer hover:text-gray-900">
                    {PAID_BY_LABEL[who]}
                </button>
            ))}
        </span>
    );
};

export const TabPill = ({ tab, size }) => {
    if (!tab) return null;
    if (/non-payable/i.test(tab)) return <Pill tone="red" size={size}>Non-payable</Pill>;
    if (/black/i.test(tab)) return <Pill tone="amber" size={size}>Black points</Pill>;
    return <Pill tone="green" size={size}>Payable</Pill>;
};

export const ImagePill = ({ fine, size }) => {
    if (!fine.has_image) return <Pill tone="red" size={size}>No image</Pill>;
    if (fine.screenshot_mode === 'offline') return <Pill tone="amber" size={size}>Offline card</Pill>;
    return <Pill tone="green" size={size}>RTA</Pill>;
};

/** Labelled control (kit Field: wires the label to the control). */
export const Field = ({ label, children, className }) => (
    <KitField label={label} className={className}>{children}</KitField>
);

/** "W28656" -> "W 28656": show plates the way the company writes them. */
export const formatPlate = (plate) => formatPlateNumber(plate);   // the one plate format (utils/plateFormatter)

function pageList(page, count) {
    if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
    const list = [...new Set([1, count, page - 1, page, page + 1])].filter((p) => p >= 1 && p <= count).sort((a, b) => a - b);
    const out = [];
    list.forEach((p, i) => {
        if (i > 0 && p - list[i - 1] > 1) out.push(`gap-${p}`);
        out.push(p);
    });
    return out;
}

/** Server pagination in the DataTable footer look. */
export const Pager = ({ page, pages, total, onPage }) => (
    <div className="flex flex-1 flex-wrap items-center gap-3">
        <span aria-live="polite">{Number(total || 0).toLocaleString()} results · Page {page} of {pages}</span>
        <span className="flex-1" />
        {pages > 1 && (
            <nav className="ds-pager" aria-label="Pagination">
                <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
                    <Icon glyph={ICONS.chevronLeft} size="xs" />
                </button>
                {pageList(page, pages).map((p) => (typeof p === 'string'
                    ? <span key={p} className="px-1">…</span>
                    : <button key={p} type="button" onClick={() => onPage(p)} aria-current={p === page ? 'page' : undefined}>{p}</button>))}
                <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next page">
                    <Icon glyph={ICONS.chevronRight} size="xs" />
                </button>
            </nav>
        )}
    </div>
);
